import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { IS_ATT_HOST, ATT_BASE, attRedirectTarget } from "@/lib/attHost";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { captureUTM, track, getVisitorId } from "@/lib/api";
import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import { WhatsAppFloat, MobileStickyBar } from "@/components/marketing/Conversion";
import Home from "@/pages/Home";
import Pricing from "@/pages/Pricing";
import LandingPage from "@/pages/LandingPage";
import Solutions from "@/pages/Solutions";
import { BlogList, BlogPost } from "@/pages/Blog";
import FAQ from "@/pages/FAQ";
import Demo from "@/pages/Demo";
import { LoginPage, SignupPage } from "@/pages/Auth";
import AppDashboard from "@/pages/AppDashboard";
import Subscription from "@/pages/Subscription";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/AdminLogin";
import AttendanceLogin from "@/pages/AttendanceLogin";
import AttendanceLayout from "@/pages/attendance/AttendanceLayout";
import AttDashboard from "@/pages/attendance/AttDashboard";
import AttBatches from "@/pages/attendance/AttBatches";
import AttRegister from "@/pages/attendance/AttRegister";
import AttView from "@/pages/attendance/AttView";
import AttEnhanced from "@/pages/attendance/AttEnhanced";
import AttEnhanceLab from "@/pages/attendance/AttEnhanceLab";
import { AttSoftware, AttError, AttCenters } from "@/pages/attendance/AttMisc";
import AttMigration from "@/pages/attendance/AttMigration";
import { SiteProvider } from "@/context/SiteContext";
import { LANDING_PAGES, LP_PAGES } from "@/lib/content";

function Tracker() {
  const location = useLocation();
  useEffect(() => {
    captureUTM();
    getVisitorId();
    track("page_view");
  }, [location.pathname]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
}

const NO_CHROME = ["/app", "/admin", "/attendance"];

function OldAttendanceRedirect() {
  const location = useLocation();
  useEffect(() => {
    const target = attRedirectTarget(location.pathname, location.search, location.hash);
    if (target) window.location.replace(target);
  }, [location]);
  return null;
}

function Chrome({ children }) {
  const location = useLocation();
  const bare = IS_ATT_HOST || NO_CHROME.some((p) => location.pathname.startsWith(p));
  if (bare) return children;
  return (
    <>
      <Header />
      <main className="pb-20 md:pb-0">{children}</main>
      <Footer />
      <WhatsAppFloat />
      <MobileStickyBar />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SiteProvider>
        <BrowserRouter>
          <Tracker />
          <OldAttendanceRedirect />
          <Chrome>
            <Routes>
              {!IS_ATT_HOST && <>
              <Route path="/" element={<Home />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/solutions/:slug" element={<Solutions />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/app" element={<AppDashboard />} />
              <Route path="/app/subscription" element={<Subscription />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/legal/:slug" element={<Legal />} />
              {Object.keys(LP_PAGES).map((slug) => (
                <Route key={slug} path={`/lp/${slug}`} element={<LandingPage lpMode slug={slug} />} />
              ))}
              {Object.keys(LANDING_PAGES).map((slug) => (
                <Route key={slug} path={`/${slug}`} element={<LandingPage slug={slug} />} />
              ))}
              </>}
              <Route path={ATT_BASE || "/"} element={<AttendanceLogin />} />
              <Route path={`${ATT_BASE}/app`} element={<AttendanceLayout />}>
                <Route index element={<AttDashboard />} />
                <Route path="batches" element={<AttBatches />} />
                <Route path="candidates/register" element={<AttRegister />} />
                <Route path="candidates/view" element={<AttView />} />
                <Route path="candidates/enhanced" element={<AttEnhanced />} />
                <Route path="candidates/enhance-lab" element={<AttEnhanceLab />} />
                <Route path="software" element={<AttSoftware />} />
                <Route path="error" element={<AttError />} />
                <Route path="centers" element={<AttCenters />} />
                <Route path="migration" element={<AttMigration />} />
              </Route>
              <Route path="*" element={IS_ATT_HOST ? <Navigate to="/" replace /> : <NotFound />} />
            </Routes>
          </Chrome>
        </BrowserRouter>
        </SiteProvider>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
